import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiCall } from '../lib/api';

type AuthType = 'none' | 'basic' | 'bearer' | 'api_key_header' | 'api_key_query';
type HeartbeatFormAuthType = AuthType | 'inherit';
type HeartbeatMethod = 'GET' | 'POST';

interface HeartbeatRecord {
  id: number;
  partner_id: string;
  name: string;
  enabled: boolean;
  method: HeartbeatMethod;
  url: string;
  body_template: any | null;
  headers: Record<string, string> | null;
  auth_type_override: AuthType | null;
  auth_username_override: string | null;
  auth_username_override_secret_ref: string | null;
  auth_password_override: string | null;
  auth_password_override_secret_ref: string | null;
  has_auth_password_override?: boolean;
  auth_token_override: string | null;
  auth_token_override_secret_ref: string | null;
  has_auth_token_override?: boolean;
  auth_header_name_override: string | null;
  auth_query_param_name_override: string | null;
  interval_seconds: number;
  timeout_ms: number;
  expected_status_codes: number[];
  last_attempt_at: string | null;
  last_success_at: string | null;
  last_error_at: string | null;
  last_status_code: number | null;
  last_error_message: string | null;
  consecutive_failures: number;
}

interface TestResult {
  success: boolean;
  statusCode?: number;
  responseBody?: string;
  responseBodyExcerpt?: string;
  targetUrl?: string;
  responseTimeMs: number;
  error?: string;
}

interface HeartbeatsEditorProps {
  partnerId: string;
}

interface HeartbeatFormState {
  name: string;
  enabled: boolean;
  method: HeartbeatMethod;
  url: string;
  interval_seconds: string;
  timeout_ms: string;
  expected_status_codes: string;
  body_template: string;
  headers: string;
  auth_type_override: HeartbeatFormAuthType;
  auth_username_override: string;
  auth_password_override: string;
  auth_token_override: string;
  auth_header_name_override: string;
  auth_query_param_name_override: string;
}

const AUTH_OPTIONS: Array<{ value: HeartbeatFormAuthType; label: string; hint: string }> = [
  { value: 'inherit', label: 'Inherit Partner Auth', hint: 'Reuse the partner-level auth settings unless a field below overrides them.' },
  { value: 'basic', label: 'Basic Auth', hint: 'Override username/password for this heartbeat.' },
  { value: 'bearer', label: 'Bearer Token', hint: 'Send Authorization: Bearer <token>.' },
  { value: 'api_key_header', label: 'API Key (header)', hint: 'Use a custom header such as X-API-Key.' },
  { value: 'api_key_query', label: 'API Key (URL param)', hint: 'Append the token as a query parameter.' },
  { value: 'none', label: 'No Auth', hint: 'Force this heartbeat to send no credentials.' },
];

const emptyForm: HeartbeatFormState = {
  name: '',
  enabled: true,
  method: 'POST',
  url: '',
  interval_seconds: '180',
  timeout_ms: '10000',
  expected_status_codes: '200, 201, 204',
  body_template: '',
  headers: '',
  auth_type_override: 'inherit',
  auth_username_override: '',
  auth_password_override: '',
  auth_token_override: '',
  auth_header_name_override: 'X-API-Key',
  auth_query_param_name_override: 'api_key',
};

function toJsonText(value: any | null): string {
  return value == null ? '' : JSON.stringify(value, null, 2);
}

function parseJsonField(value: string, fieldName: string): any | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    throw new Error(`${fieldName} must be valid JSON`);
  }
}

function parseExpectedStatuses(value: string): number[] {
  const parsed = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => Number(entry));

  if (parsed.length === 0 || parsed.some((entry) => !Number.isInteger(entry) || entry < 100 || entry > 599)) {
    throw new Error('Expected status codes must be a comma-separated list of HTTP status integers');
  }

  return parsed;
}

function formFromHeartbeat(heartbeat: HeartbeatRecord): HeartbeatFormState {
  return {
    name: heartbeat.name,
    enabled: heartbeat.enabled,
    method: heartbeat.method,
    url: heartbeat.url,
    interval_seconds: String(heartbeat.interval_seconds),
    timeout_ms: String(heartbeat.timeout_ms),
    expected_status_codes: heartbeat.expected_status_codes.join(', '),
    body_template: toJsonText(heartbeat.body_template),
    headers: toJsonText(heartbeat.headers),
    auth_type_override: heartbeat.auth_type_override ?? 'inherit',
    auth_username_override: heartbeat.auth_username_override ?? '',
    auth_password_override: '',
    auth_token_override: '',
    auth_header_name_override: heartbeat.auth_header_name_override || 'X-API-Key',
    auth_query_param_name_override: heartbeat.auth_query_param_name_override || 'api_key',
  };
}

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  busyLabel?: string;
  onLabel?: string;
  offLabel?: string;
  ariaLabel: string;
}

/**
 * Standalone on/off toggle. Replaces the earlier `<input type="checkbox" />
 * + "Enabled/Disabled" text label` pattern, which read as "this control is
 * disabled" at a glance because the empty-checkbox-with-"Disabled"-next-to-it
 * is visually indistinguishable from a greyed-out control.
 *
 * Renders a pill with a sliding knob (emerald = on, slate = off) and the
 * state label; the whole pill is the button, keyboard reachable, with an
 * explicit role=switch / aria-checked so screen readers announce state
 * rather than "checkbox".
 */
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  busyLabel,
  onLabel = 'Enabled',
  offLabel = 'Disabled',
  ariaLabel,
}) => {
  const label = busyLabel ?? (checked ? onLabel : offLabel);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium transition
        ${checked
          ? 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          : 'border-gray-300 bg-white text-gray-500 hover:bg-gray-50'}
        disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      <span
        aria-hidden="true"
        className={`relative inline-block h-4 w-8 rounded-full transition-colors
          ${checked ? 'bg-emerald-500' : 'bg-gray-300'}`}
      >
        <span
          className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform
            ${checked ? 'translate-x-[18px]' : 'translate-x-0.5'}`}
        />
      </span>
      {label}
    </button>
  );
};

function heartbeatStatus(heartbeat: HeartbeatRecord): { label: string; className: string } {
  if (!heartbeat.enabled) {
    return { label: 'Disabled', className: 'bg-gray-100 text-gray-600' };
  }

  const successAt = heartbeat.last_success_at ? new Date(heartbeat.last_success_at).getTime() : 0;
  const errorAt = heartbeat.last_error_at ? new Date(heartbeat.last_error_at).getTime() : 0;

  if (successAt && successAt >= errorAt) {
    return { label: 'Healthy', className: 'bg-emerald-50 text-emerald-700' };
  }
  if (errorAt) {
    return { label: 'Failing', className: 'bg-red-50 text-red-700' };
  }
  return { label: 'Idle', className: 'bg-amber-50 text-amber-700' };
}

function dateLabel(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '-';
}

export const HeartbeatsEditor: React.FC<HeartbeatsEditorProps> = ({ partnerId }) => {
  const [heartbeats, setHeartbeats] = useState<HeartbeatRecord[]>([]);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingHeartbeat, setEditingHeartbeat] = useState<HeartbeatRecord | null>(null);
  const [form, setForm] = useState<HeartbeatFormState>(emptyForm);
  const [testResults, setTestResults] = useState<Record<number, TestResult>>({});

  const loadHeartbeats = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiCall(`/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load heartbeats');
      }
      setHeartbeats(data.heartbeats || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void loadHeartbeats();
  }, [loadHeartbeats]);

  const editingPasswordStored = !!editingHeartbeat?.has_auth_password_override;
  const editingTokenStored = !!editingHeartbeat?.has_auth_token_override;

  const authHint = useMemo(
    () => AUTH_OPTIONS.find((option) => option.value === form.auth_type_override)?.hint || '',
    [form.auth_type_override],
  );

  const openCreateForm = () => {
    setEditingHeartbeat(null);
    setForm(emptyForm);
    setFormError(null);
    setMessage(null);
    setShowForm(true);
  };

  const openEditForm = (heartbeat: HeartbeatRecord) => {
    setEditingHeartbeat(heartbeat);
    setForm(formFromHeartbeat(heartbeat));
    setFormError(null);
    setMessage(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingHeartbeat(null);
    setForm(emptyForm);
    setFormError(null);
  };

  const saveHeartbeat = async () => {
    setSaving(true);
    setFormError(null);
    setMessage(null);

    try {
      const parsedBodyTemplate = parseJsonField(form.body_template, 'Body template');
      const parsedHeaders = parseJsonField(form.headers, 'Headers');
      const body: Record<string, any> = {
        name: form.name,
        enabled: form.enabled,
        method: form.method,
        url: form.url,
        interval_seconds: Number(form.interval_seconds),
        timeout_ms: Number(form.timeout_ms),
        expected_status_codes: parseExpectedStatuses(form.expected_status_codes),
        body_template: parsedBodyTemplate,
        headers: parsedHeaders,
        auth_type_override: form.auth_type_override === 'inherit' ? null : form.auth_type_override,
      };

      const authModeChanged = editingHeartbeat
        ? (editingHeartbeat.auth_type_override ?? 'inherit') !== form.auth_type_override
        : form.auth_type_override !== 'inherit';

      if (!editingHeartbeat || authModeChanged || form.auth_username_override !== (editingHeartbeat.auth_username_override ?? '')) {
        body.auth_username_override = form.auth_username_override;
      }
      if (form.auth_password_override) {
        body.auth_password_override = form.auth_password_override;
      }
      if (form.auth_token_override) {
        body.auth_token_override = form.auth_token_override;
      }
      if (!editingHeartbeat || authModeChanged || form.auth_header_name_override !== (editingHeartbeat.auth_header_name_override ?? '')) {
        body.auth_header_name_override = form.auth_header_name_override;
      }
      if (
        !editingHeartbeat ||
        authModeChanged ||
        form.auth_query_param_name_override !== (editingHeartbeat.auth_query_param_name_override ?? '')
      ) {
        body.auth_query_param_name_override = form.auth_query_param_name_override;
      }

      const endpoint = editingHeartbeat
        ? `/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats/${editingHeartbeat.id}`
        : `/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats`;
      const response = await apiCall(endpoint, {
        method: editingHeartbeat ? 'PUT' : 'POST',
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to save heartbeat');
      }

      setMessage(editingHeartbeat ? 'Heartbeat updated.' : 'Heartbeat created.');
      closeForm();
      await loadHeartbeats();
    } catch (err: any) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteHeartbeat = async (heartbeat: HeartbeatRecord) => {
    if (!window.confirm(`Delete heartbeat "${heartbeat.name}"?`)) return;

    setMessage(null);
    setTestError(null);
    const response = await apiCall(
      `/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats/${heartbeat.id}`,
      { method: 'DELETE' },
    );
    const data = await response.json();
    if (!response.ok) {
      setError(data.error || 'Failed to delete heartbeat');
      return;
    }

    setMessage('Heartbeat deleted.');
    setTestResults((current) => {
      const next = { ...current };
      delete next[heartbeat.id];
      return next;
    });
    await loadHeartbeats();
  };

  const toggleHeartbeatEnabled = async (heartbeat: HeartbeatRecord, enabled: boolean) => {
    setTogglingId(heartbeat.id);
    setError(null);
    setFormError(null);
    setTestError(null);
    setMessage(null);

    try {
      const response = await apiCall(
        `/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats/${heartbeat.id}`,
        {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update heartbeat');
      }

      setMessage(`Heartbeat "${heartbeat.name}" ${enabled ? 'enabled' : 'disabled'}.`);
      await loadHeartbeats();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setTogglingId(null);
    }
  };

  const sendNow = async (heartbeat: HeartbeatRecord) => {
    setTestingId(heartbeat.id);
    setTestError(null);
    setMessage(null);

    try {
      const response = await apiCall(
        `/api/config/partners/${encodeURIComponent(partnerId)}/heartbeats/${heartbeat.id}/test`,
        { method: 'POST' },
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Heartbeat test failed');
      }

      setTestResults((current) => ({
        ...current,
        [heartbeat.id]: data,
      }));
      setMessage(`Heartbeat "${heartbeat.name}" executed.`);
      await loadHeartbeats();
    } catch (err: any) {
      setTestError(err.message);
    } finally {
      setTestingId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Heartbeats
            <span className="ml-2 text-sm font-normal text-gray-400">({heartbeats.length} configured)</span>
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Schedule outbound keepalive requests per partner. Relative URLs resolve against the partner backend URL.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded((current) => !current)}
            className="px-3 py-1.5 border rounded-lg text-xs text-gray-600 hover:bg-gray-50"
          >
            {expanded ? 'Collapse' : 'Expand'}
          </button>
          <button
            onClick={() => {
              if (!expanded) setExpanded(true);
              openCreateForm();
            }}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
          >
            Add Heartbeat
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>}
      {formError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{formError}</div>}
      {testError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{testError}</div>}
      {message && <div className="text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded">{message}</div>}

      {!expanded ? (
        <p className="text-sm text-gray-400">Heartbeat editor collapsed.</p>
      ) : null}

      {expanded && showForm && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              {editingHeartbeat ? `Edit "${editingHeartbeat.name}"` : 'New Heartbeat'}
            </h3>
            <button onClick={closeForm} className="text-xs text-gray-500 hover:text-gray-700">
              Cancel
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="heartbeat-name" className="block text-xs font-medium text-gray-500 mb-1">Name</label>
              <input
                id="heartbeat-name"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="heartbeat-url" className="block text-xs font-medium text-gray-500 mb-1">URL</label>
              <input
                id="heartbeat-url"
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="/api/v2/heartbeat or https://example.com/ping"
                value={form.url}
                onChange={(event) => setForm((current) => ({ ...current, url: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="heartbeat-method" className="block text-xs font-medium text-gray-500 mb-1">Method</label>
              <select
                id="heartbeat-method"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.method}
                onChange={(event) => setForm((current) => ({ ...current, method: event.target.value as HeartbeatMethod }))}
              >
                <option value="POST">POST</option>
                <option value="GET">GET</option>
              </select>
            </div>
            <div className="flex items-end">
              <ToggleSwitch
                checked={form.enabled}
                ariaLabel={form.enabled ? 'Disable this heartbeat' : 'Enable this heartbeat'}
                onChange={(next) => setForm((current) => ({ ...current, enabled: next }))}
              />
            </div>
            <div>
              <label htmlFor="heartbeat-interval-seconds" className="block text-xs font-medium text-gray-500 mb-1">Interval (seconds)</label>
              <input
                id="heartbeat-interval-seconds"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.interval_seconds}
                onChange={(event) => setForm((current) => ({ ...current, interval_seconds: event.target.value }))}
              />
            </div>
            <div>
              <label htmlFor="heartbeat-timeout-ms" className="block text-xs font-medium text-gray-500 mb-1">Timeout (ms)</label>
              <input
                id="heartbeat-timeout-ms"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.timeout_ms}
                onChange={(event) => setForm((current) => ({ ...current, timeout_ms: event.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="heartbeat-expected-status-codes" className="block text-xs font-medium text-gray-500 mb-1">Expected Status Codes</label>
              <input
                id="heartbeat-expected-status-codes"
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder="200, 201, 204"
                value={form.expected_status_codes}
                onChange={(event) => setForm((current) => ({ ...current, expected_status_codes: event.target.value }))}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="heartbeat-auth-type" className="block text-xs font-medium text-gray-500 mb-1">Authentication</label>
              <select
                id="heartbeat-auth-type"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={form.auth_type_override}
                onChange={(event) => setForm((current) => ({ ...current, auth_type_override: event.target.value as HeartbeatFormAuthType }))}
              >
                {AUTH_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-400">{authHint}</p>
            </div>

            {form.auth_type_override === 'basic' && (
              <>
                <div>
                  <label htmlFor="heartbeat-auth-username" className="block text-xs font-medium text-gray-500 mb-1">Username Override</label>
                  <input
                    id="heartbeat-auth-username"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.auth_username_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_username_override: event.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="heartbeat-auth-password" className="block text-xs font-medium text-gray-500 mb-1">
                    Password Override <span className="text-gray-400">(leave blank to keep current)</span>
                    {editingPasswordStored && <span className="ml-2 text-emerald-600">stored</span>}
                  </label>
                  <input
                    id="heartbeat-auth-password"
                    type="password"
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.auth_password_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_password_override: event.target.value }))}
                  />
                </div>
              </>
            )}

            {form.auth_type_override === 'bearer' && (
              <div className="md:col-span-2">
                <label htmlFor="heartbeat-auth-token" className="block text-xs font-medium text-gray-500 mb-1">
                  Bearer Token Override <span className="text-gray-400">(leave blank to keep current)</span>
                  {editingTokenStored && <span className="ml-2 text-emerald-600">stored</span>}
                </label>
                <input
                  id="heartbeat-auth-token"
                  type="password"
                  className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                  value={form.auth_token_override}
                  onChange={(event) => setForm((current) => ({ ...current, auth_token_override: event.target.value }))}
                />
              </div>
            )}

            {form.auth_type_override === 'api_key_header' && (
              <>
                <div>
                  <label htmlFor="heartbeat-auth-header-name" className="block text-xs font-medium text-gray-500 mb-1">Header Name Override</label>
                  <input
                    id="heartbeat-auth-header-name"
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.auth_header_name_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_header_name_override: event.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="heartbeat-auth-header-token" className="block text-xs font-medium text-gray-500 mb-1">
                    Token Override <span className="text-gray-400">(leave blank to keep current)</span>
                    {editingTokenStored && <span className="ml-2 text-emerald-600">stored</span>}
                  </label>
                  <input
                    id="heartbeat-auth-header-token"
                    type="password"
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.auth_token_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_token_override: event.target.value }))}
                  />
                </div>
              </>
            )}

            {form.auth_type_override === 'api_key_query' && (
              <>
                <div>
                  <label htmlFor="heartbeat-auth-query-name" className="block text-xs font-medium text-gray-500 mb-1">Query Param Override</label>
                  <input
                    id="heartbeat-auth-query-name"
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.auth_query_param_name_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_query_param_name_override: event.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="heartbeat-auth-query-token" className="block text-xs font-medium text-gray-500 mb-1">
                    Token Override <span className="text-gray-400">(leave blank to keep current)</span>
                    {editingTokenStored && <span className="ml-2 text-emerald-600">stored</span>}
                  </label>
                  <input
                    id="heartbeat-auth-query-token"
                    type="password"
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.auth_token_override}
                    onChange={(event) => setForm((current) => ({ ...current, auth_token_override: event.target.value }))}
                  />
                </div>
              </>
            )}

            <div className="md:col-span-2">
              <label htmlFor="heartbeat-body-template" className="block text-xs font-medium text-gray-500 mb-1">
                Body Template (JSON)
              </label>
              <textarea
                id="heartbeat-body-template"
                rows={6}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder={
                  '{"device_id":"{{partner_id}}","timestamp":"{{now}}","tenant":"{{tenant_external_id}}","status":"online"}'
                }
                value={form.body_template}
                onChange={(event) => setForm((current) => ({ ...current, body_template: event.target.value }))}
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="heartbeat-headers" className="block text-xs font-medium text-gray-500 mb-1">Headers (JSON)</label>
              <textarea
                id="heartbeat-headers"
                rows={4}
                className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                placeholder={'{"X-Heartbeat-Source":"ops-ui"}'}
                value={form.headers}
                onChange={(event) => setForm((current) => ({ ...current, headers: event.target.value }))}
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={saveHeartbeat}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingHeartbeat ? 'Save Heartbeat' : 'Create Heartbeat'}
            </button>
            <button
              onClick={closeForm}
              className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {expanded && loading ? (
        <p className="text-sm text-gray-400">Loading heartbeats...</p>
      ) : expanded && heartbeats.length === 0 ? (
        <p className="text-sm text-gray-400">
          No heartbeats configured yet. Add one to schedule outbound keepalive requests for this partner.
        </p>
      ) : expanded ? (
        <div className="space-y-3">
          {heartbeats.map((heartbeat) => {
            const status = heartbeatStatus(heartbeat);
            const testResult = testResults[heartbeat.id];

            return (
              <div key={heartbeat.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-gray-900">{heartbeat.name}</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${status.className}`}>
                        {status.label}
                      </span>
                      <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                        {heartbeat.method}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mt-1 font-mono break-all">{heartbeat.url}</div>
                  </div>

                  <div className="flex gap-2 shrink-0">
                    <ToggleSwitch
                      checked={heartbeat.enabled}
                      disabled={togglingId === heartbeat.id}
                      busyLabel={togglingId === heartbeat.id ? 'Saving…' : undefined}
                      ariaLabel={`${heartbeat.enabled ? 'Disable' : 'Enable'} heartbeat ${heartbeat.name}`}
                      onChange={(next) => {
                        void toggleHeartbeatEnabled(heartbeat, next);
                      }}
                    />
                    <button
                      onClick={() => sendNow(heartbeat)}
                      disabled={testingId === heartbeat.id}
                      className="px-3 py-1.5 border rounded-lg text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {testingId === heartbeat.id ? 'Sending...' : 'Send now'}
                    </button>
                    <button
                      onClick={() => openEditForm(heartbeat)}
                      className="px-3 py-1.5 border rounded-lg text-xs text-gray-700 hover:bg-gray-50"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => void deleteHeartbeat(heartbeat)}
                      className="px-3 py-1.5 border border-red-200 text-red-600 rounded-lg text-xs hover:bg-red-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-500">
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Interval</div>
                    <div className="text-sm text-gray-700">{heartbeat.interval_seconds}s</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Timeout</div>
                    <div className="text-sm text-gray-700">{heartbeat.timeout_ms}ms</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Expected Status</div>
                    <div className="text-sm text-gray-700">{heartbeat.expected_status_codes.join(', ')}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Last Success</div>
                    <div className="text-sm text-gray-700">{dateLabel(heartbeat.last_success_at)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Last Error</div>
                    <div className="text-sm text-gray-700">{dateLabel(heartbeat.last_error_at)}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-gray-400">Failures</div>
                    <div className="text-sm text-gray-700">
                      {heartbeat.last_status_code ? `status ${heartbeat.last_status_code}, ` : ''}
                      {heartbeat.consecutive_failures} consecutive
                    </div>
                  </div>
                </div>

                {heartbeat.last_error_message && (
                  <div className="text-xs text-red-700 bg-red-50 rounded px-3 py-2">
                    {heartbeat.last_error_message}
                  </div>
                )}

                {testResult && (
                  <div className="text-xs bg-gray-50 rounded px-3 py-2 space-y-1">
                    <div className="font-medium text-gray-700">
                      Latest send-now result: {testResult.success ? 'success' : 'failure'}
                      {testResult.statusCode ? ` (status ${testResult.statusCode})` : ''}
                      {` in ${testResult.responseTimeMs}ms`}
                    </div>
                    {testResult.targetUrl && (
                      <div className="font-mono text-gray-500 break-all">{testResult.targetUrl}</div>
                    )}
                    {(testResult.responseBody || testResult.responseBodyExcerpt) && (
                      <pre className="whitespace-pre-wrap text-gray-600 font-mono text-[11px]">
                        {testResult.responseBody || testResult.responseBodyExcerpt}
                      </pre>
                    )}
                    {testResult.error && (
                      <div className="text-red-600">{testResult.error}</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
